import React, {useEffect, useMemo, useState} from 'react';
import {Box, styled, Typography} from '@mui/material';
import {DataGrid} from '@mui/x-data-grid';
import dayjs from 'dayjs';
import {useClient} from '../core/client';

const StyledGridOverlay = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: theme.palette.text.secondary,
}));

const NoActivityOverlay = () => (
    <StyledGridOverlay>
        <Typography variant="body2">No recent activity yet.</Typography>
    </StyledGridOverlay>
);

const TYPE_LABELS = {
    DEFENDANT_CREATED: 'Defendant Created',
    CHECK_IN: 'Check-in Completed',
    LOCATION_PING: 'Location Ping',
};

const ENTITY_LABELS = {
    Defendant: 'Defendant',
    User: 'User',
};

const toTitleCase = (value) => {
    if (!value) return '';
    return value
        .toLowerCase()
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

const formatLabel = (value, map) => {
    if (!value) return '';
    if (map?.[value]) {
        return map[value];
    }
    return toTitleCase(value.replace(/[_-]/g, ' '));
};

const columns = [
    { field: 'typeLabel', headerName: 'Type', width: 180 },
    { field: 'message', headerName: 'Message', flex: 1, minWidth: 250 },
    { field: 'entityLabel', headerName: 'Entity', width: 150 },
    { field: 'createdAtLabel', headerName: 'Date', width: 220 },
];

const RecentActivityGrid = () => {
    const client = useClient();
    const [activity, setActivity] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchRecentActivity = async () => {
            setLoading(true);
            try {
                const { data } = await client.get('/activity-log/recent');
                setActivity(Array.isArray(data) ? data : []);
            } catch (error) {
                console.error('Failed to load recent activity', error);
                setActivity([]);
            } finally {
                setLoading(false);
            }
        };

        fetchRecentActivity();
    }, [client]);

    const rows = useMemo(
        () =>
            activity
                .filter(Boolean)
                .map((entry, index) => {
                    const createdAt = entry.createdAt ?? entry.timestamp ?? null;
                    return {
                        id: entry._id || entry.id || entry.entityId || createdAt || index,
                        typeLabel: formatLabel(entry.type, TYPE_LABELS),
                        entityLabel: formatLabel(entry.entityType, ENTITY_LABELS),
                        createdAtLabel: createdAt
                            ? dayjs(createdAt).format('MMM D, YYYY h:mm A')
                            : '',
                        message: entry.message || '',
                    };
                }),
        [activity]
    );

    return (
        <Box sx={{ width: '100%', height: 400 }}>
            <DataGrid
                rows={rows}
                columns={columns}
                loading={loading}
                disableSelectionOnClick
                hideFooter
                slots={{
                    noRowsOverlay: NoActivityOverlay,
                }}
            />
        </Box>
    );
};

export default RecentActivityGrid;
